import { C_ON_SURFACE, C_PRIMARY, FontSize, getFontStyle } from "@schaeffler-commerce/design-system";
import { GlobalStyles as MuiGlobalStyles, type Theme } from "@schaeffler-commerce/design-system";

/**
 * Migrated from styles/style.less (global resets + base element styles).
 *
 * The `@layer resets, utilities, external-content` ORDER declaration is
 * load-bearing (the utilities/external-content layers must stay ordered
 * below unlayered CSS); it is emitted here as an empty-block at-rule, the
 * same mechanism St4ContentGlobalStyles uses for its layer body.
 *
 * Layered `@layer resets` rules lose against ALL unlayered CSS regardless
 * of source order — exactly as they did inside main.css.
 *
 * Deliberately NOT carried over from style.less:
 * - `@media print` block: its `@page 330mm 427mm` was already overridden by
 *   PrintGlobalStyles' A4 page (emotion injects after main.css), and the
 *   `.container` print width was already overridden by the emotion utility
 *   `.container` rules — moving them into emotion would FLIP the winner and
 *   change today's observable print behavior. The `.container` print width
 *   is preserved in PrintGlobalStyles; the losing @page is dropped.
 * - `a.disabled:not(.scha__link)`: dead — the only renderer of a `disabled`
 *   class on anchors is Link.tsx, which always also renders `scha__link`
 *   (excluded by :not); no JSP/backend storefront markup carries it.
 */
export const globalResetStyles = (_theme: Theme) => ({
  // Layer order declaration (empty block, emitted as-is).
  "@layer resets, utilities, external-content": {},

  "@layer resets": {
    "*, *:before, *:after": {
      boxSizing: "border-box",
    },

    "*": {
      margin: 0,
      padding: 0,
      // @font from variables.less
      fontFamily: '"Noto Sans", Arial, sans-serif !important',
    },

    html: {
      WebkitTapHighlightColor: "rgba(0, 0, 0, 0)",
    },

    "h1, h2, h3, h4, h5, h6, p": {
      margin: 0,
      fontWeight: "inherit",
      color: "inherit",
    },

    button: {
      textAlign: "unset",
    },

    // from mixins.less (.resetButtonStyle)
    ".btn-style-reset": {
      background: "none",
      border: "none",
      fontWeight: "inherit",
    },
  },

  body: {
    ...getFontStyle(FontSize.BodyMedium),
    color: C_ON_SURFACE,
    minHeight: "100vh",
    overflowX: "hidden",
    position: "relative",
    backgroundColor: "#ffffff",
    hyphens: "auto",
  },

  // Reset fonts for relevant elements
  "input, button, select, textarea": {
    fontFamily: "inherit",
    fontSize: "inherit",
    lineHeight: "inherit",
  },

  // Nuke the outline - RIP accessibility, but client hates outline.
  // Inputs have a dedicated focused style, links and buttons in general do not.
  "a, input, textarea, button, div": {
    outline: "none",
  },

  textarea: {
    // override bootstrap...
    minHeight: "auto",
  },

  a: {
    textDecoration: "none",
    color: C_PRIMARY,
  },

  header: {
    background: "#ffffff",
  },

  img: {
    maxWidth: "100%",
    maxHeight: "100%",
  },

  label: {
    // Reset bootstrap styles...
    ...getFontStyle(FontSize.LabelLarge),
    color: C_ON_SURFACE,
    textTransform: "none",
    fontWeight: "normal",
    display: "inline-flex",
  },

  "input::-ms-clear": {
    display: "none",
  },

  "input, textarea, select": {
    outline: "none",
  },

  "[hidden]": {
    display: "none !important",
  },

  ".MuiFormHelperText-root.MuiFormHelperText-root": {
    letterSpacing: "unset",
  },
});

export const GlobalResetStyles = () => <MuiGlobalStyles styles={globalResetStyles} />;
